const normalizeNumber = (value) => Number(value || 0);

const cloneOptionGroups = (groups = []) => (
  (Array.isArray(groups) ? groups : []).map((group) => ({
    ...group,
    options: (Array.isArray(group?.options) ? group.options : []).map((option) => ({
      ...option,
      price: option?.price && typeof option.price === 'object'
        ? { ...option.price }
        : option?.price,
      logosUrls: Array.isArray(option?.logosUrls) ? [...option.logosUrls] : option?.logosUrls,
      product: option?.product && typeof option.product === 'object'
        ? {
          ...option.product,
          logosUrls: Array.isArray(option.product.logosUrls) ? [...option.product.logosUrls] : option.product.logosUrls,
        }
        : option?.product,
    })),
  }))
);

export const cloneProductDraft = (product = {}) => ({
  ...product,
  itemPrice: product.itemPrice && typeof product.itemPrice === 'object'
    ? { ...product.itemPrice }
    : { value: normalizeNumber(product.unitPrice) },
  logosUrls: Array.isArray(product.logosUrls) ? [...product.logosUrls] : [],
  itemOptionGroups: cloneOptionGroups(product.itemOptionGroups),
});

export const updateProductOptionGroup = (product = {}, groupIndex, changes = {}) => ({
  ...product,
  itemOptionGroups: (product.itemOptionGroups || []).map((group, index) => (
    index === groupIndex ? { ...group, ...changes } : group
  )),
});

export const updateProductOption = (product = {}, groupIndex, optionIndex, changes = {}) => ({
  ...product,
  itemOptionGroups: (product.itemOptionGroups || []).map((group, currentGroupIndex) => (
    currentGroupIndex !== groupIndex
      ? group
      : {
        ...group,
        options: (group.options || []).map((option, currentOptionIndex) => (
          currentOptionIndex === optionIndex ? { ...option, ...changes } : option
        )),
      }
  )),
});

const sameJson = (left, right) => JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
const OPTION_IDENTITY_FIELDS = [
  'externalCode', 'itemExternalCode', 'integrationCode', 'optionId', 'id',
  'productId', 'itemId', 'optionProductId',
];
const GROUP_IDENTITY_FIELDS = ['optionGroupId', 'groupId', 'id', 'externalCode'];

const newLocalId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') crypto.getRandomValues(bytes);
  else for (let index = 0; index < bytes.length; index += 1) bytes[index] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export const createSharedOptionGroupDraft = () => ({
  brutosSharedGroupId: newLocalId(),
  brutosSharedVersion: 1,
  name: 'Novo grupo de complementos',
  description: '',
  minQuantity: 0,
  maxQuantity: 1,
  options: [createSharedOptionDraft()],
});

export const createSharedOptionDraft = () => ({
  brutosSharedOptionId: newLocalId(),
  name: '',
  description: '',
  price: { value: 0 },
  showInKds: true,
});

const preserveIdentityFields = (original = {}, draft = {}, fields = []) => {
  const next = { ...draft };
  fields.forEach((field) => {
    if (Object.prototype.hasOwnProperty.call(original, field)) next[field] = original[field];
    else delete next[field];
  });
  return next;
};

const identityMatch = (candidate, original, fields) => fields.some((field) => (
  candidate?.[field] != null && original?.[field] != null
  && String(candidate[field]) === String(original[field])
));

const preserveOptionGroupIdentities = (originalGroups = [], draftGroups = []) => (
  draftGroups.map((draftGroup) => {
    const matchedGroup = originalGroups.find((candidate) => (
      (draftGroup.brutosSharedGroupId && candidate.brutosSharedGroupId === draftGroup.brutosSharedGroupId)
      || identityMatch(draftGroup, candidate, GROUP_IDENTITY_FIELDS)
    ));
    const originalGroup = matchedGroup || {};
    const group = matchedGroup
      ? preserveIdentityFields(originalGroup, draftGroup, GROUP_IDENTITY_FIELDS)
      : { ...draftGroup };
    return {
      ...group,
      brutosSharedGroupId: draftGroup.brutosSharedGroupId || originalGroup.brutosSharedGroupId,
      brutosSharedVersion: draftGroup.brutosSharedVersion || originalGroup.brutosSharedVersion,
      options: (draftGroup.options || []).map((draftOption) => {
        const matchedOption = (originalGroup.options || []).find((candidate) => (
          (draftOption.brutosSharedOptionId && candidate.brutosSharedOptionId === draftOption.brutosSharedOptionId)
          || identityMatch(draftOption, candidate, OPTION_IDENTITY_FIELDS)
        ));
        const originalOption = matchedOption || {};
        return {
          ...(matchedOption ? preserveIdentityFields(originalOption, draftOption, OPTION_IDENTITY_FIELDS) : draftOption),
          brutosSharedOptionId: draftOption.brutosSharedOptionId || originalOption.brutosSharedOptionId,
        };
      }),
    };
  })
);

const EDITOR_SCALARS = [
  ['name', 'itemName', (value) => String(value || '')],
  ['description', 'itemDescription', (value) => String(value || '')],
  ['catalogGroupId', 'catalogGroupId', (value) => value ? Number(value) : null],
  ['price', 'itemPrice', (value) => normalizeNumber(value?.value)],
  ['stock', 'stock', normalizeNumber],
  ['itemMinSalePrice', 'itemMinSalePrice', normalizeNumber],
  ['itemQuantity', 'itemQuantity', normalizeNumber],
  ['itemUnit', 'itemUnit', (value) => String(value || '')],
  ['preparationDescription', 'preparationDescription', (value) => String(value || '')],
  ['kdsName', 'kdsName', (value) => String(value || '')],
  ['showInKds', 'showInKds', (value) => value !== false],
  ['showInBalcao', 'showInBalcao', (value) => value !== false],
];

export const buildProductEditorChanges = (original = {}, draft = {}) => {
  const changes = {};
  EDITOR_SCALARS.forEach(([requestField, productField, normalize]) => {
    const before = normalize(original[productField]);
    const after = normalize(draft[productField]);
    if (!Object.is(before, after)) changes[requestField] = after;
  });

  const previousLogos = Array.isArray(original.logosUrls) ? original.logosUrls : [];
  const nextLogos = Array.isArray(draft.logosUrls) ? draft.logosUrls : [];
  if (!sameJson(previousLogos, nextLogos)) {
    changes.logosUrls = nextLogos;
    changes.imageUrl = draft.imageUrl || nextLogos[0] || '';
  } else if (!sameJson(original.imageUrl || null, draft.imageUrl || null)) {
    changes.imageUrl = draft.imageUrl || '';
  }

  const previousGroups = Array.isArray(original.itemOptionGroups) ? original.itemOptionGroups : [];
  const nextGroups = preserveOptionGroupIdentities(
    previousGroups,
    Array.isArray(draft.itemOptionGroups) ? draft.itemOptionGroups : []
  );
  if (!sameJson(previousGroups, nextGroups)) changes.itemOptionGroups = nextGroups;

  return changes;
};

const stripCreationIdentities = (groups = []) => groups.map((group) => {
  const cleanGroup = { ...group };
  GROUP_IDENTITY_FIELDS.forEach((field) => delete cleanGroup[field]);
  cleanGroup.options = (group.options || []).map((option) => {
    const cleanOption = { ...option };
    OPTION_IDENTITY_FIELDS.forEach((field) => delete cleanOption[field]);
    return cleanOption;
  });
  return cleanGroup;
});

export const buildManualProductCreationChanges = (draft = {}) => ({
  name: String(draft.itemName || '').trim(),
  price: normalizeNumber(draft.itemPrice?.value),
  stock: normalizeNumber(draft.stock),
  description: String(draft.itemDescription || ''),
  catalogGroupId: draft.catalogGroupId ? Number(draft.catalogGroupId) : null,
  imageUrl: String(draft.imageUrl || draft.logosUrls?.[0] || ''),
  logosUrls: Array.isArray(draft.logosUrls) ? draft.logosUrls : [],
  itemOptionGroups: stripCreationIdentities(Array.isArray(draft.itemOptionGroups) ? draft.itemOptionGroups : []),
  itemMinSalePrice: normalizeNumber(draft.itemMinSalePrice),
  itemQuantity: normalizeNumber(draft.itemQuantity),
  itemUnit: String(draft.itemUnit || ''),
  preparationDescription: String(draft.preparationDescription || ''),
  kdsName: String(draft.kdsName || '').trim(),
  showInKds: draft.showInKds !== false,
  showInBalcao: draft.showInBalcao !== false,
  isActive: true,
});

export const createCatalogMutationId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `catalog-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const applyOptionIdentityUpdates = (product = {}, updates = []) => (
  (Array.isArray(updates) ? updates : []).reduce((current, update) => {
    if (!Number.isInteger(update?.groupIndex)) return current;
    if (Number.isInteger(update.optionIndex)) {
      return updateProductOption(current, update.groupIndex, update.optionIndex, {
        ...(update.optionId ? { optionId: update.optionId } : {}),
        ...(update.externalCode ? { externalCode: update.externalCode } : {}),
      });
    }
    return updateProductOptionGroup(current, update.groupIndex, {
      ...(update.optionGroupId ? { optionGroupId: update.optionGroupId } : {}),
    });
  }, product)
);
